import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useHistory } from 'react-router'
import { Button, Segment } from 'semantic-ui-react'
import { ContentHeader } from '../../../../components/Header/ContentHeader'
import { RootState } from '../../../../store/rootState'
import { currencyFormat } from '../../../../utils/currencyFormat'
import ServiceProviderDCProgress from '../../components/accounting/ServiceProviderDCProgress'
import ServiceProviderPTCProgress from '../../components/accounting/ServiceProviderPTCProgress'
import SponsorProgress from '../../components/accounting/SponsorProgress'
import { IProjectBudgetCost } from '../../types/costs'
import { IPassThroughCost } from '../../types/directAndPassTroughCosts'
import { IDirectCostInvoice } from '../../types/invoice'
import { IProject } from '../../types/project'
import { calculateProjectOfferTotalWithRebate } from "../../../../utils/calculations"

interface Props {
  project: IProject
  budgetCosts: IProjectBudgetCost[]
  passThroughCosts: IPassThroughCost[]
  accountingTab: string
  directCostInvoices: IDirectCostInvoice[]
}

const AccountingHeaderContainer = (props: Props): JSX.Element | null => {

  const { project, budgetCosts, passThroughCosts, accountingTab, directCostInvoices } = props

  const [sponsorWidgetValues, setSponsorWidgetValues] = useState<{ paid: number, canceled: number, remaining: number, total: number }>({
    paid: 0,
    canceled: 0,
    remaining: 0,
    total: 0
  })

  const [servProvPTCWidgetValues, setServProvPTCWidgetValues] = useState<{ paid: number, canceled: number, receivable: number, remaining: number, total: number }>({
    paid: 0,
    receivable: 0,
    canceled: 0,
    remaining: 0,
    total: 0
  })

  const [servProvDCWidgetValues, setServProvDCWidgetValues] = useState<{ paid: number, remaining: number, total: number }>({
    paid: 0,
    remaining: 0,
    total: 0
  })

  const _history = useHistory()

  useEffect(() => {
    if (project.type === 'sponsor' || (project.type === 'service_provider' && accountingTab === 'external_costs')) {
      const vals = {
        paid: 0,
        canceled: 0,
        remaining: 0,
        total: 0
      }
      if (budgetCosts.length > 0) {
        budgetCosts.forEach(cost => {
          vals.total += +cost.total_cost
          vals.paid += +cost.thereof_paid
          if (cost.position_status === 'closed') {
            vals.canceled += (+cost.total_cost - +cost.thereof_paid)
          }
        })
      }
      vals.remaining = vals.total - (vals.paid + vals.canceled)
      if (project.accounting) {
        vals.total -= vals.canceled
      }
      setSponsorWidgetValues(vals)
    }
    if (accountingTab === 'pass_through_costs') {
      const vals = {
        paid: 0,
        receivable: 0,
        canceled: 0,
        remaining: 0,
        total: 0
      }
      if (passThroughCosts.length > 0) {
        let totalPaid = 0
        passThroughCosts.forEach(ptc => {
          const ptcTotal = +ptc.unit_price * +ptc.quantity
          vals.total += ptcTotal
          totalPaid += +ptc.thereof_paid
          vals.paid += +ptc.passed_through_amount

          if (ptc.position_status === 'closed') {
            vals.canceled += (ptcTotal - +ptc.thereof_paid)
          }
        })
        setServProvPTCWidgetValues({ ...vals, receivable: totalPaid - vals.paid, remaining: vals.total - totalPaid - vals.canceled })
      } else {
        setServProvPTCWidgetValues(vals)
      }
    }
    if (accountingTab === 'direct_costs') {
      const vals = {
        paid: 0,
        remaining: 0,
        total: calculateProjectOfferTotalWithRebate(project)
      }
      if (directCostInvoices.length > 0) {
        directCostInvoices.forEach(invoice => {
          if (invoice.paid_on) {
            // Paid is what was actually invoiced, never discounted (issue #120). The widget
            // therefore reads gross paid against the rebated study total — the same ratio the
            // accounting page's "% of Total" footer shows.
            vals.paid += +invoice.invoiced_amount
          }
        })
      }
      vals.remaining = vals.total - vals.paid
      setServProvDCWidgetValues(vals)
    }

  }, [budgetCosts, accountingTab, passThroughCosts, directCostInvoices, project.type, project.accounting])

  const showPayments = () => {
    const locArr = location.pathname.split('/').filter(path => path !== '')
    if (accountingTab === 'pass_through_costs') {
      _history.push(`/projects/${project.id}/accounting/invoice-list`, { from: locArr[locArr.length - 1] })
    } else {
      _history.push(`/projects/${project.id}/accounting/payment-list`, { from: locArr[locArr.length - 1], tab: accountingTab })
    }
  }

  const renderSponsorTotals = () => {
    return (
      <React.Fragment>
        <div style={{ display: 'flex', float: 'left', color: '#FB8682', fontWeight: 'bold', fontSize: '1.4rem' }}>
          <p style={{ margin: 0 }}>{currencyFormat(+sponsorWidgetValues.paid)[0]} {currencyFormat(+sponsorWidgetValues.paid).split(',')[0].replace(currencyFormat(+sponsorWidgetValues.paid)[0], '')}</p>
          <p style={{ color: '#BEC8C3' }}>,{currencyFormat(+sponsorWidgetValues.paid).split(',')[1]}</p>
        </div>
        <div style={{ color: '#747678' }}>/</div>
        <div style={{ display: 'flex', alignItems: 'center', float: 'left', color: '#747678', fontWeight: 'bold', fontSize: '1.1rem' }}>
          <p style={{ margin: 0, marginTop: '0.2rem' }}>{currencyFormat(sponsorWidgetValues.total)[0]} {currencyFormat(sponsorWidgetValues.total).split(',')[0].replace(currencyFormat(project.financial_calculation.total_budget ? +project.financial_calculation.total_budget : 0)[0], '')}</p>
          <p style={{ margin: 0, marginTop: '0.2rem' }}>,{currencyFormat(sponsorWidgetValues.total).split(',')[1]}</p>
        </div>
      </React.Fragment>
    )
  }

  const renderPTCTotals = () => {
    return (
      <React.Fragment>
        <div style={{ display: 'flex', float: 'left', color: accountingTab === 'pass_through_costs' ? '#B882FB' : '#FB8682', fontWeight: 'bold', fontSize: '1.4rem' }}>
          <p style={{ margin: 0 }}>{currencyFormat(+servProvPTCWidgetValues.paid)[0]} {currencyFormat(+servProvPTCWidgetValues.paid).split(',')[0].replace(currencyFormat(+servProvPTCWidgetValues.paid)[0], '')}</p>
          <p style={{ color: '#BEC8C3' }}>,{currencyFormat(+servProvPTCWidgetValues.paid).split(',')[1]}</p>
        </div>
        <div style={{ color: '#747678' }}>/</div>
        <div style={{ display: 'flex', alignItems: 'center', float: 'left', color: '#747678', fontWeight: 'bold', fontSize: '1.1rem' }}>
          <p style={{ margin: 0, marginTop: '0.2rem' }}>{currencyFormat(servProvPTCWidgetValues.total)[0]} {currencyFormat(servProvPTCWidgetValues.total).split(',')[0].replace(currencyFormat(project.financial_calculation.total_budget ? +project.financial_calculation.total_budget : 0)[0], '')}</p>
          <p style={{ margin: 0, marginTop: '0.2rem' }}>,{currencyFormat(servProvPTCWidgetValues.total).split(',')[1]}</p>
        </div>
      </React.Fragment>
    )
  }

  const renderDCTotals = () => {
    return (
      <React.Fragment>
        <div style={{ display: 'flex', float: 'left', color: '#08D6A1', fontWeight: 'bold', fontSize: '1.4rem' }}>
          <p style={{ margin: 0 }}>{currencyFormat(+servProvDCWidgetValues.paid)[0]} {currencyFormat(+servProvDCWidgetValues.paid).split(',')[0].replace(currencyFormat(+servProvDCWidgetValues.paid)[0], '')}</p>
          <p style={{ color: '#BEC8C3' }}>,{currencyFormat(+servProvDCWidgetValues.paid).split(',')[1]}</p>
        </div>
        <div style={{ color: '#747678' }}>/</div>
        <div style={{ display: 'flex', alignItems: 'center', float: 'left', color: '#747678', fontWeight: 'bold', fontSize: '1.1rem' }}>
          <p style={{ margin: 0, marginTop: '0.2rem' }}>{currencyFormat(servProvDCWidgetValues.total)[0]} {currencyFormat(servProvDCWidgetValues.total).split(',')[0].replace(currencyFormat(project.financial_calculation.total_budget ? +project.financial_calculation.total_budget : 0)[0], '')}</p>
          <p style={{ margin: 0, marginTop: '0.2rem' }}>,{currencyFormat(servProvDCWidgetValues.total).split(',')[1]}</p>
        </div>
      </React.Fragment>
    )
  }

  const renderWidget = () => {
    if (project.type === 'service_provider' && accountingTab === 'pass_through_costs') {
      return <ServiceProviderPTCProgress widgetValues={servProvPTCWidgetValues} total_budget={project.financial_calculation.total_budget ? +project.financial_calculation.total_budget : 0} />
    }
    if (project.type === 'service_provider' && accountingTab === 'direct_costs') {
      return <ServiceProviderDCProgress widgetValues={servProvDCWidgetValues} total_budget={servProvDCWidgetValues.total} />
    }
    if (project.type === 'sponsor' || (project.type === 'service_provider' && accountingTab === 'external_costs')) {
      return <SponsorProgress widgetValues={sponsorWidgetValues} total_budget={project.financial_calculation.total_budget ? +project.financial_calculation.total_budget : 0} />
    }
    return null
  }

  const renderTotals = () => {
    if (accountingTab === 'pass_through_costs') {
      return renderPTCTotals()
    } else if (accountingTab === 'direct_costs') {
      return renderDCTotals()
    } else {
      return renderSponsorTotals()
    }
  }

  const paymentsWidget = () => {
    return (
      <Segment style={{ border: 'none', borderRadius: '.57rem', boxShadow: '0 0 25px -5px #15151511', width: 'auto', minWidth: '25rem', height: '8.2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
          <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
            <p style={{ color: '#172533', fontWeight: 'bold', margin: 0, fontSize: '14px' }}>
              {accountingTab === 'direct_costs' ? 'Invoice Completion' : accountingTab === 'pass_through_costs' ? 'Pass Through Status' : 'Payment Status'} </p>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '1.2rem' }}>
              {renderTotals()}
            </div>
          </div>
          {accountingTab !== 'direct_costs' ? <div>
            <Button
              inverted
              size='small'
              color='teal'
              content={accountingTab === 'pass_through_costs' ? 'Show Invoices' : 'Show Payments'}
              onClick={showPayments}
              style={{ fontWeight: 400, height: '29px', padding: '8px 5px', color: '#08D6A1', borderRadius: '.34rem', backgroundColor: 'transparent' }}
            />
          </div> : null}
        </div>

        {renderWidget()}
      </Segment>
    )
  }

  return (
    <ContentHeader
      id='projectActivitiesHeader'
      title={project.name}
      color='#08D6A1'
      backLocation='/projects'
      backTitle='Accounting'
      subtitle={project.customer?.name ?? project.category}
      column={true}
      rightItemsFirst={paymentsWidget()}
      marginTop='-.4rem'
    />
  )
}

const mapStateToProps = (state: RootState): Props => {
  return {
    project: state.projectsState.project,
    budgetCosts: state.projectsState.budgetCosts,
    passThroughCosts: state.projectsState.passTroughCosts,
    accountingTab: state.projectsState.accountingTab,
    directCostInvoices: state.projectsState.directCostInvoices
  }
}

export default connect(mapStateToProps, null)(AccountingHeaderContainer)
